// @ts-strict-ignore
import { ProjectModel } from '../../../model/project.model';
import { ProjectStatesDef } from '../../project/constants/project-states-def';
import { SaveProjectService } from '@project/components/project/project-save/save-project.service';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { ProjectService } from '../../import-export/service/project.service';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectHeadVO, ProjectSaveInfoVO, ProjectVO, RequestFinalizeProjectResultVO } from '../../../model/vo/project.vo';
import { StringUtils } from '@shared/util/string-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { UserSettings } from '@user/model/user-settings';
import { ArticlePreviewUtils } from '@article/util/article-preview.utils';
import { FinalizationData } from '@project/components/request/processor/finalization-data';
import { Injectable } from '@angular/core';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectDataService } from '../../../service/project-data.service';
import { ProjectRequestProcessorDelegator } from './project-request-processor.delegator';
import { ProjectComponentPresenter } from '../../project-component.presenter';
import { ArticleNumberLong } from '../../../model/article-number-long';
import { lastValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ProjectRequestProcessor {
  private totalDemand: number;
  private batchSize: number;
  private deliveryDate: Date;
  private emailAddress: string;
  private remarks: string;
  private sapProjectNumber: string;
  private orderDirectly: boolean;
  private projectsForMultipleRequest: ProjectHeadVO[];
  private handleProjectSaved: boolean = false;
  private additionalEmails: string[];
  public projectSaveInfo: ProjectSaveInfoVO;

  constructor(
    projectRequestProcessorDelegator: ProjectRequestProcessorDelegator,
    private userSettings: UserSettings,
    private projectModel: ProjectModel,
    private projectModelService: ProjectModelService,
    private saveProjectService: SaveProjectService,
    private alertHelper: AlertHelper,
    private projectService: ProjectService,
    private projectDataService: ProjectDataService,
    private localeResourceManager: LocaleResourceManager,
    private projectComponentPresenter: ProjectComponentPresenter,
  ) {
    projectRequestProcessorDelegator.projectRequestProcessor = this;
  }

  public startProjectRequest(): void {
    if (this.projectModel.currentProject != null) {
      this.totalDemand = isNaN(this.projectModel.currentProject.totalDemand) ? 0 : this.projectModel.currentProject.totalDemand;
      this.batchSize = isNaN(this.projectModel.currentProject.batchSize) ? 0 : this.projectModel.currentProject.batchSize;
    } else {
      this.totalDemand = 0;
      this.batchSize = 0;
    }
    this.deliveryDate = null;
    this.showProjectRequestPopup();
  }

  public showProjectRequestPopup(): void {
    this.projectDataService
      .loadProjectSaveInfo(this.projectModel.currentProject.id, this.projectModel.currentProject.currentVersion)
      .toPromise()
      .then((projectSaveInfo: ProjectSaveInfoVO) => {
        this.projectSaveInfo = projectSaveInfo;
      });
    this.projectComponentPresenter.openProjectRequestQuoteTypeDialog();
  }

  public async requestFinalizeProject(finalizationData: FinalizationData, isRequestSingleQuoteMode: boolean = true): Promise<void> {
    this.totalDemand = finalizationData.totalDemand;
    this.batchSize = finalizationData.batchSize;
    this.deliveryDate = finalizationData.deliveryDate;
    this.sapProjectNumber = finalizationData.sapProjectNumber;
    this.remarks = finalizationData.remarks;
    this.emailAddress = finalizationData.emailAddress;
    this.orderDirectly = finalizationData.orderDirectly;
    this.additionalEmails = finalizationData.additionalEmails;
    this.projectsForMultipleRequest = finalizationData.projectsForMultipleRequest;
    if (isRequestSingleQuoteMode) {
      if (this.projectModel.currentProjectRequiresSaving) {
        await this.prepareProjectForSaving();
      }
      this.dispatchRequestFinalizeProjectMessage();
    } else {
      const currentProjectIsIncludedInFinalization: boolean = !!finalizationData.projectsForMultipleRequest.find(
        (project: ProjectVO) => {
          return project.id === this.projectModel.currentProject.id
        }
      );
      if (currentProjectIsIncludedInFinalization && this.projectModel.currentProjectRequiresSaving) {
        await this.prepareProjectForSaving();
      }
      this.dispatchRequestFinalizeMultipleProjectsMessage();
    }
  }

  private dispatchRequestFinalizeMultipleProjectsMessage(): void {
    const finalizationData: FinalizationData = new FinalizationData({
      totalDemand: this.totalDemand,
      batchSize: this.batchSize,
      deliveryDate: this.deliveryDate,
      remarks: this.remarks,
      sapProjectNumber: this.sapProjectNumber,
      emailAddress: this.emailAddress,
      orderDirectly: this.orderDirectly,
      additionalEmails: this.additionalEmails,
      projectsForMultipleRequest: this.projectsForMultipleRequest,
    });
    this.projectDataService
      .requestFinalizeMultipleProjects(finalizationData, this.localeResourceManager.currentLanguage.langKey)
      .toPromise()
      .then(() => this.requestFinalizeMultipleProjectsSucceeded())
      .catch(() => this.requestFinalizeProjectFailed());
  }

  private async prepareProjectForSaving(): Promise<void> {
    if (this.projectModel.currentProject.finalizationState < ProjectStatesDef.FINALIZATION_STATE_REQUESTED) {
      await this.saveProjectService.prepareSaveProject(null, null, null);
    } else {
      this.alertHelper.showError('requestFinalization.title.finalizationError', 'requestFinalization.message.finalizationError');
    }
  }

  public saveProjectSucceeded(): void {
    if (!this.handleProjectSaved) {
      return;
    }
    this.handleProjectSaved = false;
    this.dispatchRequestFinalizeProjectMessage();
  }

  private dispatchRequestFinalizeProjectMessage(): void {
    if (this.projectModel.currentProjectRequiresSaving) {
      this.handleProjectSaved = true;
      return;
    }
      this.projectDataService
        .requestFinalizeProject(
          this.projectModel.currentProject.id,
          this.projectModel.currentProject.currentVersion,
          this.emailAddress,
          this.batchSize,
          this.deliveryDate,
          this.remarks,
          this.sapProjectNumber,
          this.totalDemand,
          false,
          this.localeResourceManager.currentLanguage.langKey,
          this.orderDirectly,
          this.additionalEmails
        )
        .toPromise()
      .then((requestFinalizeProjectResultVO) => this.requestFinalizeSucceeded(requestFinalizeProjectResultVO))
      .catch((e) => this.requestFinalizeProjectFailed());
  }

  private requestFinalizeMultipleProjectsSucceeded(): void {
    this.projectService.updateProjectData(this.projectModel.currentProject.id, this.projectModel.currentProject.currentVersion);
    this.saveAdditionalRequestEmailProperties(this.additionalEmails);
    this.alertHelper.showAlert(
      'requestFinalization.title.finalizationSuccess',
      this.localeResourceManager.getValueByKey('requestFinalization.message.finalizationSuccess_multiple'),
      null,
      [AlertButton.OK],
      "info"
    );
  }

  private requestFinalizeSucceeded(result: RequestFinalizeProjectResultVO): void {
    this.projectModel.currentProject.batchSize = this.batchSize;
    this.projectModel.currentProject.totalDemand = this.totalDemand;
    if (result != null && result.resultState === 'waitingOnDuplicateDecision') {
      if (!result.duplicateArticleNumbers) {
        this.requestFinalizeProjectFailed();
        return;
      }
      this.getDuplicateArticleNumbersSucceeded(result.duplicateArticleNumbers.map((value) => value.articleNumber));
    } else {
      this.projectModel.currentProject.finalizationState = ProjectStatesDef.FINALIZATION_STATE_REQUESTED;
      this.projectModelService.notifyFinalizationStateChanged();
      if (result != null) {
        this.projectModel.currentProject.isArticleNumberGenerated = result.isArticleNumberGenerated;
        if (result.projectArticleNumberInfo) {
          this.projectModel.currentProject.articleNumber = result.projectArticleNumberInfo.articleNumber;
          this.projectModel.currentProject.articleNumberLong = new ArticleNumberLong(result.projectArticleNumberInfo.articleNumberLong);
          this.projectModel.currentProject.drawingNumber = result.projectArticleNumberInfo.drawingNumber;
        }
      }
      this.saveAdditionalRequestEmailProperties(this.additionalEmails);
      let successMessageText: string;
      if (StringUtils.isEmpty(this.projectModel.currentProject.articleNumberLong.reducedArticleNumberLong)) {
        successMessageText = this.localeResourceManager.getValueByKey('requestFinalization.message.finalizationSuccess_no_article_number');
      } else {
        successMessageText = this.localeResourceManager.getValueByKey('requestFinalization.message.finalizationSuccess', [
          this.projectModel.currentProject.articleNumberLong.reducedArticleNumberLong,
        ]);
      }
      this.alertHelper.showAlert('requestFinalization.title.finalizationSuccess', successMessageText, null, [AlertButton.OK], "info");
    }
  }

  private saveAdditionalRequestEmailProperties(lastAdditionalEmails: string[]): void {
    this.userSettings.setUserSettingValue("lastAdditionalRequestEmails", lastAdditionalEmails);
    this.userSettings.saveUserSettings();
  }

  public requestFinalizeProjectFailed(): void {
    this.projectService.updateProjectData(this.projectModel.currentProject.id, this.projectModel.currentProject.currentVersion);
    this.alertHelper.showError('requestFinalization.title.finalizationError', 'requestFinalization.message.finalizationError');
  }

  public getDuplicateArticleNumbersSucceeded(articleNumbers: string[]): void {
    if (!articleNumbers) {
      this.requestFinalizeProjectFailed();
    }
    const duplicateArticleNumber: string = ArrayUtils.isEmpty(articleNumbers) ? '' : articleNumbers[0];
    this.alertHelper
      .showAlert(
        'requestFinalizeProject.duplicateFound.title',
        this.localeResourceManager.getValueByKey('requestFinalizeProject.duplicateFound.text', [duplicateArticleNumber]),
        null,
        [AlertButton.YES, AlertButton.NO, AlertButton.CANCEL],
        "help",
        {
          yesLabel: 'requestFinalizeProject.duplicateFound.label.continue',
          noLabel: 'requestFinalizeProject.duplicateFound.label.order_in_eshop',
        }
      )
      .then((answer) => {
        if (answer === AlertButton.YES) {
          lastValueFrom(this.projectDataService
            .requestFinalizeProject(
              this.projectModel.currentProject.id,
              this.projectModel.currentProject.currentVersion,
              this.emailAddress,
              this.batchSize,
              this.deliveryDate,
              this.remarks,
              this.sapProjectNumber,
              this.totalDemand,
              true,
              this.localeResourceManager.currentLanguage.langKey,
              this.orderDirectly,
              this.additionalEmails
            ))
            .then((requestFinalizeProjectResultVO) => this.requestFinalizeSucceeded(requestFinalizeProjectResultVO))
            .catch((e) => this.requestFinalizeProjectFailed());
        } else if (answer === AlertButton.NO) {
          ArticlePreviewUtils.openShopLink(duplicateArticleNumber, this.localeResourceManager);
        }
      });
  }

  public changeFinalizationStateSucceeded() {
    this.projectService.updateProjectData(this.projectModel.currentProject.id, this.projectModel.currentProject.currentVersion);
    this.alertHelper.showAlert(
      'chooseFinalizationAction.setFinalizationStateSuccess.title',
      'chooseFinalizationAction.setFinalizationStateSuccess.label',
      null,
      [AlertButton.OK],
      "class-ok"
    );
  }

  public changeFinalizationStateFailed(error: Error) {
    this.projectService.updateProjectData(this.projectModel.currentProject.id, this.projectModel.currentProject.currentVersion);
    const message: string = this.localeResourceManager.getValueByKey('chooseFinalizationAction.setFinalizationStateError.label') + ' ' + error?.message;
    this.alertHelper.showAlert('global.title.error', message, null, [AlertButton.OK], "exclamation");
  }
}
